import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db, functions } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  onSnapshot,
  updateDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const Configuracoes: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [notificacoesHabilitadas, setNotificacoesHabilitadas] = useState(true);
  const [loadingExcluir, setLoadingExcluir] = useState(false);
  const [solicitacaoAtiva, setSolicitacaoAtiva] = useState<any | null>(null);
  const [empresaNome, setEmpresaNome] = useState<string>('');
  const [empresaFoto, setEmpresaFoto] = useState<string>('');
  const [empresaId, setEmpresaId] = useState<string>('');
  const [showMenu, setShowMenu] = useState(false);

  // Carregar dados da empresa
  useEffect(() => {
    const carregarEmpresa = async () => {
      try {
        const userId = await storage.getItem('userId');
        if (userId) {
          const userDoc = await getDoc(doc(db, 'usuarios', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const empId = userData.empresaId;
            if (empId) {
              setEmpresaId(empId);
              const empresaDoc = await getDoc(doc(db, 'Empresa', empId));
              if (empresaDoc.exists()) {
                const empresaData = empresaDoc.data();
                setEmpresaNome(empresaData.nomeEmpresa || empresaData.nome || 'Empresa');
                setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar nome da empresa:', error);
      }
    };
    carregarEmpresa();
  }, []);

  // Assina solicitação de exclusão ativa
  useEffect(() => {
    let unsub: (() => void) | undefined;
    const sub = async () => {
      const userId = await storage.getItem('userId');
      if (!userId) return;
      try {
        const q = query(
          collection(db, 'solicitacoes_exclusao'),
          where('userId', '==', userId),
          where('status', 'in', ['pendente', 'saldo_transferido']),
          orderBy('criadoEm', 'desc'),
          limit(1)
        );
        unsub = onSnapshot(q, (snap) => {
          if (!snap.empty) {
            const d = snap.docs[0];
            setSolicitacaoAtiva({ id: d.id, ...d.data() });
          } else {
            setSolicitacaoAtiva(null);
          }
        });
      } catch (e) {
        console.error('Erro ao assinar solicitações:', e);
      }
    };
    sub();
    return () => {
      if (unsub) unsub();
    };
  }, []);

  const cancelarSolicitacao = async () => {
    try {
      if (!solicitacaoAtiva?.id) return;
      await updateDoc(doc(db, 'solicitacoes_exclusao', solicitacaoAtiva.id), {
        status: 'rejeitada',
        canceladoPor: 'usuario',
        canceladoEm: serverTimestamp(),
      });
      alert('Solicitação cancelada. Sua solicitação de exclusão foi cancelada.');
    } catch (e) {
      alert('Erro: Não foi possível cancelar a solicitação.');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const confirmarExclusao = () => {
    if (!confirm('Esta ação é permanente e apagará seus dados de conta. Se houver saldo na carteira, a solicitação será enviada para análise da central. Deseja continuar?')) {
      return;
    }

    const executarExclusao = async () => {
      try {
        setLoadingExcluir(true);
        const userId = await storage.getItem('userId');
        let saldo = 0;
        let empresaIdLocal: string | null = null;

        if (userId) {
          try {
            const userSnap = await getDoc(doc(db, 'usuarios', userId));
            if (userSnap.exists()) {
              const udata = userSnap.data() as any;
              empresaIdLocal = udata.empresaId || null;
            }
          } catch {}
        }

        if (empresaIdLocal) {
          try {
            const empresaSnap = await getDoc(doc(db, 'Empresa', empresaIdLocal));
            if (empresaSnap.exists()) {
              const edata = empresaSnap.data() as any;
              saldo = Number(edata?.saldoCarteira || 0);
            }
          } catch {}
        }

        if (saldo > 0.01 && userId) {
          try {
            // Verifica se já existe uma solicitação ativa
            const q2 = query(
              collection(db, 'solicitacoes_exclusao'),
              where('userId', '==', userId),
              where('status', 'in', ['pendente', 'saldo_transferido']),
              limit(1)
            );
            const snap2 = await getDocs(q2);
            if (!snap2.empty) {
              alert('Solicitação em andamento: Você já possui uma solicitação de exclusão ativa. Cancele-a antes de criar uma nova.');
              return;
            }
            await addDoc(collection(db, 'solicitacoes_exclusao'), {
              userId,
              criadoEm: serverTimestamp(),
              status: 'pendente',
              saldoDetectado: saldo,
            });
          } catch {}

          alert('Solicitação enviada: Sua solicitação de exclusão foi enviada à central para análise de saldo. Você será notificado após a conclusão.');
        } else {
          try {
            const callDelete = httpsCallable(functions, 'deleteUserAccount');
            await callDelete();
          } catch (callableErr: any) {
            // Fallback para HTTP se necessário
            const idToken = await storage.getItem('authToken');
            if (!idToken) {
              throw callableErr;
            }
            const resp = await fetch('https://us-central1-vs-sistemadeentregas.cloudfunctions.net/deleteUserAccountHttp', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-id-token': idToken },
              body: JSON.stringify({ idToken }),
            });
            if (!resp.ok) {
              const txt = await resp.text();
              throw new Error(txt || 'Falha HTTP ao excluir conta');
            }
          }

          alert('Conta excluída: Sua conta foi excluída com sucesso.');
          await storage.removeItem('userId');
          await storage.removeItem('authToken');
          navigate('/login');
        }
      } catch (err) {
        alert('Erro: Não foi possível excluir sua conta. Tente novamente mais tarde.');
      } finally {
        setLoadingExcluir(false);
      }
    };

    executarExclusao();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(true)}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Configurações</h1>
                <p className="text-sm text-gray-500">Gerencie suas preferências</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/empresa')}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <span>←</span>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
        {/* Aviso sobre exclusão */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-yellow-800">
                Você pode excluir sua conta a qualquer momento. Alguns registros poderão ser retidos de forma
                anonimizada por até 30 dias para fins operacionais e legais.
              </p>
            </div>
          </div>
        </div>

        {/* Preferências */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Preferências</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Notificações</p>
                  <p className="text-sm text-gray-500">Receber notificações do sistema</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificacoesHabilitadas}
                  onChange={(e) => setNotificacoesHabilitadas(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Conta */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Conta</h2>

          {/* Solicitação de exclusão ativa */}
          {solicitacaoAtiva && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-yellow-900 mb-2">Solicitação de exclusão ativa</p>
                  <p className="text-sm text-yellow-800 mb-1">
                    Status: <span className="font-medium">{solicitacaoAtiva.status}</span>
                  </p>
                  {typeof solicitacaoAtiva.saldoDetectado === 'number' && (
                    <p className="text-sm text-yellow-800">
                      Saldo detectado: R$ {Number(solicitacaoAtiva.saldoDetectado).toFixed(2).replace('.', ',')}
                    </p>
                  )}
                </div>
                <button
                  onClick={cancelarSolicitacao}
                  className="ml-4 px-4 py-2 bg-white border border-yellow-300 text-yellow-800 rounded-lg hover:bg-yellow-100 transition-colors text-sm font-semibold"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={() => navigate('/perfil')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-900">Meu Perfil</span>
              </div>
              <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => navigate('/mensalidades')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-900">Mensalidade</span>
              </div>
              <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200 hover:bg-red-100 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <span className="font-semibold text-red-600">Sair</span>
              </div>
              <svg className="w-5 h-5 text-red-400 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Privacidade */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Privacidade</h2>
          
          <button
            onClick={confirmarExclusao}
            disabled={loadingExcluir}
            className="w-full flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200 hover:bg-red-100 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                {loadingExcluir ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                ) : (
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </div>
              <span className="font-semibold text-red-600">Excluir conta</span>
            </div>
            {!loadingExcluir && (
              <svg className="w-5 h-5 text-red-400 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-500">Versão Web: 1.0.0</p>
        </div>
      </main>

      {/* Menu Lateral */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-r border-gray-200/50 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                {empresaFoto ? (
                  <img
                    src={empresaFoto}
                    alt={empresaNome}
                    className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <p className="text-lg font-bold">{empresaNome || 'Empresa'}</p>
                <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
              </div>
            </div>

            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/empresa');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/status-pedido');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Pedidos</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/perfil');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Perfil</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/relatorio');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Histórico de Entregas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/tabela-taxas');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Tabela de Taxas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/configuracoes');
                }}
                className="w-full text-left px-4 py-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Configurações</span>
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className="font-semibold text-red-600">Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default Configuracoes;

